export const WEAPON = {
  id: 'rifle',
  label: 'M4A1',
  class: 'carbine',
  caliber: '5.56x45',
  /** Free starter weapon — never enters the armory's purchase flow. */
  price: 0,
  /* --- fire control --- */
  rpm: 800,
  modes: ['auto', 'burst', 'semi'],
  burstCount: 3,
  burstRpm: 950,
  burstDelay: 0.16,
  /* --- ammunition --- */
  magSize: 30,
  reserve: 210,
  /* --- terminal ballistics --- */
  muzzleVelocity: 880,
  damage: 33,
  penetration: 1.0,
  dropoff: 0.62,
  maxRange: 420,
  dragK: 0.28,
  tracerEvery: 3,
  /* --- accuracy (degrees) --- */
  spreadHip: 2.05,
  spreadAds: 0.24,
  spreadPerShot: 0.3,
  spreadMax: 3.4,
  spreadDecay: 3.6,
  /* --- recoil --- */
  recoil: {
    pitch: 0.0085, // radians of camera climb per shot
    yaw: 0.0022,
    kickBack: 0.019, // metres the viewmodel travels rearward
    kickUp: 0.0072,
    roll: 0.032,
    punch: 0.35,
    freq: 8.5,
    damping: 0.42,
    patternLength: 30,
    patternSeed: 0x4d34a1,
    climbShape: [1.45, 1.3, 1.15, 1.05, 1.0], // first-shots multiplier
    drift: 0.55, // how much the pattern wanders horizontally
  },
  /* --- handling (seconds) --- */
  adsTime: 0.22,
  adsFov: 0.74,
  viewFov: 0.86,
  reloadTac: 2.1,
  reloadEmpty: 2.9,
  inspectTime: 3.2,
  drawTime: 0.62,
  holsterTime: 0.4,
  /* --- pose ---
   * Weapon-local origin is the web of the shooting hand (top of the grip).
   * The butt pad is at z=+0.245, the muzzle crown at z=-0.502, the optic
   * ocular at (0, 0.142, +0.006) and the mag floorplate ~150 mm below origin.
   *
   * SOLVED FROM THE BORE AXIS, not from where the optic happens to land.
   *
   * The previous pose (hipPos [0.081,-0.192,-0.215], hipRot [-0.026,0.076,
   * 0.055]) was derived by putting the OPTIC at a chosen screen position, and
   * that is the wrong constraint: it left the bore 1.5 deg nose-down with the
   * weapon only 215 mm from the eye, so the whole barrel forward of the
   * receiver ran off the top-left of the frame and the muzzle crown — where
   * the flash spawns — projected onto empty street. What reads as "the gun
   * points at the crosshair" is the MUZZLE being visible, up-left of the
   * receiver, on the way to the centre of the screen.
   *
   * Constraints, in order:
   *   1. bore axis 4.0 deg LEFT of view-forward (converging on the crosshair)
   *      and 2.9 deg nose-down:  rx = -0.050, ry = +0.070
   *   2. rolled 7.7 deg so the LEFT flank of the receiver (the side that
   *      carries the rollmark, the bolt catch and the port) faces the camera
   *      and the rail deck turns edge-on instead of presenting its lit top
   *      face:  rz = -0.135
   *   3. muzzle crown inside x 1050-1300, y 620-780 at 1920x1080
   *   4. optic ocular below and right of screen centre
   *   5. magazine + pistol grip in the lower-right frame
   *
   * With the rotation above the muzzle offset is (-0.025, +0.049, -0.505) and
   * the ocular offset (+0.019, +0.141, -0.003), so at a 60 deg vertical view
   * FOV (half-height 0.5774|z|, half-width 1.0264|z|):
   *   muzzle -> (1064, 698)   ocular -> (1374, 677)   magwell mouth -> (1268, 870)
   * i.e. the muzzle is 300 px up-LEFT of the optic and heading for the middle
   * of the frame, which is the read that was missing.
   *
   * z = -0.30 (was -0.215) is what makes the weapon small enough for the mag
   * and grip to enter the frame at all: the gun's vertical extent from optic
   * to floorplate is 291 mm, and at 215 mm from the eye that is 93% of the
   * frame height. It is also the limit — the support hand is then 620 mm
   * downrange of a shoulder 200 mm off the eye, and a 572 mm arm has nothing
   * left. The butt pad ends up 60 mm in FRONT of the eye but 140 mm off axis,
   * so it is outside the frustum rather than clipped by the near plane. */
  hipPos: [0.118, -0.185, -0.3],
  hipRot: [-0.05, 0.081, -0.135],
  adsCant: [0, 0, 0.004],
  /* Eye to the rear lens.
   *
   * MEASURED FROM THE ADS FRAME, not chosen for realism. Two numbers have to
   * come out right and they pull in opposite directions:
   *
   *   housing size     the 31 mm tube's outer rim subtends rOuter/relief. At
   *                    0.078 that was 256 px of radius — a 512 px ring, HALF
   *                    the frame height, and every critic called the optic
   *                    oversized. 0.115 puts it at 168 px (336 px across,
   *                    31% of frame height), which is where a modern shooter
   *                    frames a tube sight.
   *   sight picture    is stopped by the objective bore at (relief + len), so a
   *                    LONGER relief improves the picture-to-housing ratio:
   *                    (relief)/(relief+len) goes from 0.53 to 0.69.
   *
   * So both wanted the same thing and the old value was simply too close. With
   * the 52 mm tube and the flared bore (see parts.js buildOptic) this lands the
   * clear aperture at 115 px against a 168 px housing. */
  eyeRelief: 0.115,
  /* Sprint: gun dropped and angled across the body, muzzle down-left.
   * Carried over by the same delta as the hip pose so the blend does not
   * translate the weapon 90 mm sideways on the way into a sprint. */
  sprintPos: [0.09, -0.262, -0.275],
  sprintRot: [-0.4, 0.6, 0.2],
  lowReadyPos: [0.112, -0.28, -0.289],
  lowReadyRot: [-0.46, 0.125, -0.09],
  swayScale: 1,
  bobScale: 1,
  magLen: 0.212,
};

export const loadModel = () => import('../models/rifle.js').then((m) => m.buildRifle);
